import { AssistantContractTypePlanForm } from "@/components/assistant/AssistantContractTypePlanForm";
import { AssistantEditableWriteApprovalPlanForm } from "@/components/assistant/AssistantEditableWriteApprovalPlanForm";
import { supportsEditableWriteApprovalPlan } from "@/components/assistant/writeApprovalEditablePlanFields";
import { AssistantPlanCardsView } from "@/components/assistant/AssistantPlanCardsView";
import { AssistantPlanGuideline } from "@/components/assistant/AssistantPlanGuideline";
import { AssistantPlanHero } from "@/components/assistant/AssistantPlanHero";
import {
  formatWriteApprovalSummary,
  getWriteApprovalCompactPreview,
  getWriteApprovalPlanCardGroups,
  getWriteApprovalPlanGuidelineParagraph,
  getWriteApprovalPlanHeroLabels,
} from "@/components/assistant/formatWriteApprovalSummary";
import { useAssistantPlan } from "@/components/assistant/AssistantPlanContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { IconButton } from "@doculegis/shared";
import { FileText, Loader2, X } from "lucide-react";
import { useMemo } from "react";

type Props = {
  variant?: "embedded" | "standalone";
};

export function AssistantPlanPanel({ variant = "embedded" }: Props) {
  const { open, setOpen, snapshot } = useAssistantPlan();

  const cardGroups = useMemo(() => {
    if (!snapshot) return [];
    return getWriteApprovalPlanCardGroups(snapshot.kind, snapshot.mode, snapshot.proposal);
  }, [snapshot]);

  const { headline } = useMemo(() => {
    if (!snapshot) return { headline: "Plan" };
    return formatWriteApprovalSummary(snapshot.kind, snapshot.proposal);
  }, [snapshot]);

  const guidelineText = useMemo(() => {
    if (!snapshot) return "";
    return getWriteApprovalPlanGuidelineParagraph(snapshot.kind, snapshot.mode);
  }, [snapshot]);

  const heroPreview = useMemo(() => {
    if (!snapshot) return null;
    const compact = getWriteApprovalCompactPreview(snapshot.kind, snapshot.proposal);
    const labels = getWriteApprovalPlanHeroLabels(snapshot.kind);
    return { compact, labels };
  }, [snapshot]);

  if (!open || !snapshot) return null;

  const primaryLabel = snapshot.mode === "create" ? "Create" : "Update";

  const rootClass = cn(
    "flex h-full min-h-0 min-w-0 flex-col overflow-hidden",
    variant === "standalone" &&
      "rounded-2xl border border-[rgba(15,23,42,0.09)] bg-[var(--chat-shell-bg)] shadow-[var(--shadow-card)] dark:border-white/10 dark:bg-gradient-to-b dark:from-slate-800 dark:to-slate-900 dark:shadow-none lg:w-[var(--inline-assistant-chat-w)] lg:flex-none"
  );

  return (
    <div className={rootClass} role="region" aria-label="Full plan">
      <div
        className={cn(
          "flex shrink-0 items-center justify-between border-b border-[rgba(15,23,42,0.09)] px-4 py-3 dark:border-white/10",
          variant === "embedded"
            ? "bg-[var(--surface-sunken)]/50 dark:bg-white/[0.03]"
            : "bg-gradient-to-r from-blue-600/[0.08] to-purple-600/[0.08] dark:from-blue-600/20 dark:to-purple-600/20"
        )}
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg shadow-blue-500/20">
            <FileText className="h-4 w-4 text-white" />
          </div>
          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold text-[#101828] dark:text-white">
              {snapshot.planTitle || headline || "Plan"}
            </h3>
            <p className="text-xs text-[#576274] dark:text-gray-400">
              {snapshot.kind === "contract-type" || snapshot.editablePlan
                ? "Modify fields directly or ask the assistant to refine them."
                : "Full plan review"}
            </p>
          </div>
        </div>
        <IconButton
          type="button"
          size="sm"
          onClick={() => setOpen(false)}
          title="Close plan"
          aria-label="Close plan"
        >
          <X className="h-4 w-4" />
        </IconButton>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-none p-4">
        <div className="space-y-5">
          <AssistantPlanGuideline text={guidelineText} />
          {snapshot.kind === "contract-type" && snapshot.contractTypePlan ? (
            <AssistantContractTypePlanForm
              draft={snapshot.contractTypePlan.draft}
              onDraftChange={snapshot.contractTypePlan.onDraftChange}
              disabled={snapshot.disabled}
              pendingPdfs={snapshot.contractTypePlan.pendingPdfs}
              onQueuePdf={snapshot.contractTypePlan.onQueuePdf}
              onRemovePendingPdf={snapshot.contractTypePlan.onRemovePendingPdf}
              savedDocs={snapshot.contractTypePlan.savedDocs}
              getAccessToken={snapshot.contractTypePlan.getAccessToken}
              assistantProposalConflict={snapshot.contractTypePlan.assistantProposalConflict}
              onApplyAssistantProposal={snapshot.contractTypePlan.onApplyAssistantProposal}
              onKeepLocalEdits={snapshot.contractTypePlan.onKeepLocalEdits}
              onFoldReferencePdfs={snapshot.contractTypePlan.onFoldReferencePdfs}
            />
          ) : snapshot.editablePlan && supportsEditableWriteApprovalPlan(snapshot.kind) ? (
            <AssistantEditableWriteApprovalPlanForm
              kind={snapshot.kind}
              draft={snapshot.editablePlan.draft}
              onDraftChange={snapshot.editablePlan.onDraftChange}
              disabled={snapshot.disabled}
              assistantProposalConflict={snapshot.editablePlan.assistantProposalConflict}
              onApplyAssistantProposal={snapshot.editablePlan.onApplyAssistantProposal}
              onKeepLocalEdits={snapshot.editablePlan.onKeepLocalEdits}
              instructionSourceText={snapshot.editablePlan.instructionSourceText}
              onInstructionSummarized={snapshot.editablePlan.onInstructionSummarized}
              referenceDocuments={snapshot.editablePlan.referenceDocuments}
            />
          ) : (
            <>
              {heroPreview ? (
                <AssistantPlanHero
                  title={heroPreview.compact.title}
                  shortCode={heroPreview.compact.shortCode}
                  primaryLabel={heroPreview.labels.primaryLabel}
                  shortCodeLabel={heroPreview.labels.shortCodeLabel}
                />
              ) : null}
              <AssistantPlanCardsView groups={cardGroups} />
            </>
          )}
        </div>
      </div>

      <div
        className={cn(
          "flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-[rgba(15,23,42,0.09)] px-4 py-3 dark:border-white/10",
          !snapshot.approvalReady && "opacity-90"
        )}
      >
        {!snapshot.approvalReady ? (
          <div className="mr-auto flex min-w-0 items-center gap-2 text-xs text-[#576274] dark:text-gray-400">
            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
            <span className="truncate">Preparing approval…</span>
          </div>
        ) : null}
        <Button
          type="button"
          variant="default"
          size="sm"
          disabled={snapshot.disabled || snapshot.submitDisabled}
          onClick={() => snapshot.onCreate()}
        >
          {primaryLabel}
        </Button>
      </div>
    </div>
  );
}
